import { useMemo } from 'react';
import Select, {
  components,
  createFilter,
  type OptionProps,
  type SingleValueProps,
  type StylesConfig,
} from 'react-select';
import Button from 'src/components/Button';
import { useEncompassFieldSchema } from 'src/queries/encompass/useEncompassFieldSchema';
import { LoanChecklistRuleCondition } from 'src/services/loans/types';
import { VirtualizedMenuList } from './VirtualizedMenuList';

type SelectOption = { value: string; label: string };
type ConditionEditorLayout = 'wide' | 'compact';

const inputClass =
  'block h-8 w-full rounded border border-gray-300 bg-white px-2 text-xs text-gray-700 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500';
const fastFilter = createFilter({ ignoreAccents: false });
const operatorOptions: SelectOption[] = [
  { value: 'and', label: 'And' },
  { value: 'or', label: 'Or' },
];

const clipOption = <T,>(base: T) => ({
  ...base,
  whiteSpace: 'nowrap' as const,
  overflow: 'hidden' as const,
  textOverflow: 'ellipsis' as const,
});

const conditionSelectStyles: StylesConfig<SelectOption, false> = {
  control: (base, state) => ({
    ...base,
    height: 32,
    minHeight: 32,
    borderColor: state.isFocused ? '#3b82f6' : '#d1d5db',
    boxShadow: state.isFocused ? '0 0 0 1px #3b82f6' : 'none',
    '&:hover': { borderColor: state.isFocused ? '#3b82f6' : '#9ca3af' },
  }),
  valueContainer: (base) => ({ ...base, padding: '0 8px' }),
  indicatorsContainer: (base) => ({ ...base, height: 32, padding: '0 2px' }),
  indicatorSeparator: () => ({ display: 'none' }),
  option: (base) => ({ ...clipOption(base), fontSize: '0.75rem' }),
  singleValue: (base) => ({ ...clipOption(base), fontSize: '0.75rem' }),
  placeholder: (base) => ({ ...base, fontSize: '0.75rem' }),
  input: (base) => ({ ...base, fontSize: '0.75rem' }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

function TooltipOption(props: OptionProps<SelectOption, false>) {
  return (
    <components.Option {...props}>
      <span className="block truncate" title={props.data.label}>
        {props.children}
      </span>
    </components.Option>
  );
}

function TooltipSingleValue(props: SingleValueProps<SelectOption, false>) {
  return (
    <components.SingleValue {...props}>
      <span className="block truncate" title={props.data.label}>
        {props.children}
      </span>
    </components.SingleValue>
  );
}

const conditionSelectComponents = {
  MenuList: VirtualizedMenuList,
  Option: TooltipOption,
  SingleValue: TooltipSingleValue,
};

function ConditionOperatorSelect({
  inputId,
  index,
  value,
  onChange,
}: Readonly<{
  inputId: string;
  index: number;
  value: 'and' | 'or';
  onChange: (value: 'and' | 'or') => void;
}>) {
  return (
    <Select<SelectOption>
      inputId={inputId}
      aria-label={`Condition operator ${index + 1}`}
      isSearchable={false}
      options={operatorOptions}
      value={operatorOptions.find((option) => option.value === value)}
      onChange={(option) => onChange((option?.value ?? 'and') as 'and' | 'or')}
      className="text-xs"
      classNamePrefix="encompass-condition-operator"
      components={conditionSelectComponents}
      menuPortalTarget={typeof document === 'undefined' ? null : document.body}
      menuPosition="fixed"
      menuPlacement="auto"
      maxMenuHeight={220}
      styles={conditionSelectStyles}
    />
  );
}

const conditionLabel = (condition: LoanChecklistRuleCondition) =>
  condition.fieldLabel || condition.field;

function EncompassFieldValueInput({
  inputId,
  fieldId,
  value,
  onChange,
  optionChoices,
  useDateInput,
}: Readonly<{
  inputId: string;
  fieldId: string;
  value: string;
  onChange: (value: string) => void;
  optionChoices: SelectOption[];
  useDateInput: boolean;
}>) {
  if (optionChoices.length > 0) {
    const selected =
      optionChoices.find((option) => option.value === value) ??
      (value ? { value, label: value } : null);

    return (
      <Select<SelectOption>
        inputId={inputId}
        aria-label="Expected field value"
        isClearable
        options={optionChoices}
        value={selected}
        onChange={(option) => onChange(option?.value ?? '')}
        placeholder="Select allowed value…"
        className="text-xs"
        classNamePrefix="encompass-condition-value"
        components={conditionSelectComponents}
        menuPortalTarget={
          typeof document === 'undefined' ? null : document.body
        }
        menuPosition="fixed"
        menuPlacement="auto"
        maxMenuHeight={220}
        styles={conditionSelectStyles}
      />
    );
  }

  let placeholder: string | undefined;
  if (!useDateInput) {
    placeholder = 'Expected value';
    if (fieldId) {
      placeholder = 'Exact match value';
    }
  }

  return (
    <input
      id={inputId}
      type={useDateInput ? 'date' : 'text'}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      placeholder={placeholder}
      className={inputClass}
    />
  );
}

export function EncompassFieldConditionEditor({
  conditions,
  onChange,
  idPrefix,
  layout = 'wide',
}: Readonly<{
  conditions: LoanChecklistRuleCondition[];
  onChange: (conditions: LoanChecklistRuleCondition[]) => void;
  idPrefix: string;
  /** Wide is for the global-checklist modal; compact is for a rule side panel. */
  layout?: ConditionEditorLayout;
}>) {
  const fieldSchemaQuery = useEncompassFieldSchema();

  const fieldOptions = useMemo<SelectOption[]>(() => {
    const fields = fieldSchemaQuery.data?.fields ?? [];
    const options = fields.map((field) => ({
      value: field.id,
      label: field.description
        ? `${field.id} — ${field.description}`
        : field.id,
    }));
    const ids = new Set(options.map((option) => option.value));

    conditions.forEach((condition) => {
      if (!condition.field || ids.has(condition.field)) return;
      options.push({
        value: condition.field,
        label: conditionLabel(condition),
      });
    });

    return options.sort((a, b) => a.label.localeCompare(b.label));
  }, [conditions, fieldSchemaQuery.data?.fields]);

  const fieldValueOptionsById = useMemo(() => {
    const optionsById = new Map<string, SelectOption[]>();
    for (const field of fieldSchemaQuery.data?.fields ?? []) {
      const fieldId = field.id?.trim();
      if (!fieldId || !field.options?.length) continue;

      const seen = new Set<string>();
      const options = field.options.flatMap((option) => {
        const value = option.value?.trim() ?? '';
        if (!value || seen.has(value)) return [];
        seen.add(value);
        return [{ value, label: option.text?.trim() || value }];
      });
      if (options.length > 0) optionsById.set(fieldId, options);
    }
    return optionsById;
  }, [fieldSchemaQuery.data?.fields]);

  const fieldDataTypeById = useMemo(() => {
    const dataTypes = new Map<string, string>();
    for (const field of fieldSchemaQuery.data?.fields ?? []) {
      const fieldId = field.id?.trim();
      if (fieldId) dataTypes.set(fieldId, field.dataType ?? '');
    }
    return dataTypes;
  }, [fieldSchemaQuery.data?.fields]);

  const updateCondition = (
    index: number,
    updates: Partial<LoanChecklistRuleCondition>,
  ) =>
    onChange(
      conditions.map((condition, conditionIndex) =>
        conditionIndex === index ? { ...condition, ...updates } : condition,
      ),
    );

  return (
    <div className="flex flex-col gap-3">
      {conditions.map((condition, index) => (
        <div
          key={`${idPrefix}-${index}`}
          className={
            layout === 'compact'
              ? 'grid grid-cols-2 gap-2 rounded-md border border-gray-200 bg-gray-50 p-3'
              : 'grid grid-cols-1 gap-2 rounded-md border border-gray-200 bg-gray-50 p-3 sm:grid-cols-6'
          }
        >
          <div
            className={
              layout === 'compact'
                ? 'col-span-2 flex flex-col gap-2'
                : 'flex flex-col gap-2 sm:col-span-2'
            }
          >
            <label
              htmlFor={`${idPrefix}-field-${index}`}
              className="text-sm font-medium text-gray-900"
            >
              Encompass field
            </label>
            <Select<SelectOption>
              inputId={`${idPrefix}-field-${index}`}
              aria-label={`Encompass field condition ${index + 1}`}
              isClearable
              isLoading={fieldSchemaQuery.isLoading}
              options={fieldOptions}
              value={
                fieldOptions.find(
                  (option) => option.value === condition.field,
                ) ?? null
              }
              onChange={(option) => {
                const field = option?.value ?? '';
                updateCondition(index, {
                  field,
                  fieldLabel: option?.label ?? undefined,
                  value: field === condition.field ? condition.value : '',
                });
              }}
              placeholder="Search field…"
              className="text-xs"
              classNamePrefix="encompass-condition-field"
              components={conditionSelectComponents}
              filterOption={fastFilter}
              menuPortalTarget={
                typeof document === 'undefined' ? null : document.body
              }
              menuPosition="fixed"
              menuPlacement="auto"
              maxMenuHeight={220}
              styles={conditionSelectStyles}
            />
          </div>
          <div
            className={
              layout === 'compact'
                ? 'col-span-2 flex flex-col gap-2'
                : 'flex flex-col gap-2 sm:col-span-2'
            }
          >
            <label
              htmlFor={`${idPrefix}-value-${index}`}
              className="text-sm font-medium text-gray-900"
            >
              Value
            </label>
            <EncompassFieldValueInput
              inputId={`${idPrefix}-value-${index}`}
              fieldId={condition.field}
              value={condition.value}
              onChange={(value) => updateCondition(index, { value })}
              optionChoices={
                fieldValueOptionsById.get(condition.field.trim()) ?? []
              }
              useDateInput={
                (fieldDataTypeById.get(condition.field.trim()) ?? '')
                  .trim()
                  .toLowerCase() === 'datetime'
              }
            />
          </div>
          <div className="flex flex-col gap-2 sm:col-span-1">
            <label
              htmlFor={`${idPrefix}-operator-${index}`}
              className="text-sm font-medium text-gray-900"
            >
              Operator
            </label>
            <ConditionOperatorSelect
              inputId={`${idPrefix}-operator-${index}`}
              index={index}
              value={condition.operator}
              onChange={(operator) => updateCondition(index, { operator })}
            />
          </div>
          <div className="flex items-end justify-end sm:col-span-1">
            <Button
              variant="outline"
              onClick={() =>
                onChange(
                  conditions.filter(
                    (_, conditionIndex) => conditionIndex !== index,
                  ),
                )
              }
            >
              Remove
            </Button>
          </div>
        </div>
      ))}
      {fieldSchemaQuery.isError && (
        <p role="alert" className="text-xs text-red-600">
          Failed to load Encompass fields.
        </p>
      )}
      <div>
        <Button
          variant="outline"
          onClick={() =>
            onChange([...conditions, { field: '', operator: 'and', value: '' }])
          }
        >
          Add condition
        </Button>
      </div>
    </div>
  );
}
